import React, { useCallback, useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  FileDown,
  Printer,
  Filter,
  RefreshCw,
  GraduationCap,
  CheckCircle2,
  AlertCircle,
  XCircle,
  CalendarClock,
} from "lucide-react";
import { APPOINTMENT_TYPES, APPOINTMENT_TYPE_LABEL } from "@/lib/appointment-types";
import { formatDateES, todayISO, appointmentRouteLabel, studentShortLabel } from "@/lib/utils-app";
import StudentPicker from "@/components/autoescuela/StudentPicker";

const STATUS_BUCKETS = [
  { value: "all", label: "Todas" },
  { value: "scheduled", label: "Programadas" },
  { value: "done", label: "Realizadas" },
  { value: "missed", label: "No asistidas" },
  { value: "cancelled", label: "Canceladas" },
];

const GROUP_OPTIONS = [
  { value: "none", label: "Sin agrupar" },
  { value: "student", label: "Por alumno" },
  { value: "type", label: "Por tipo de cita" },
];

const STATUS_LABEL = {
  scheduled: "Programada",
  done: "Realizada",
  missed: "No asistida",
  cancelled: "Cancelada",
};

const STATUS_PILL = {
  scheduled: "bg-amber-100 text-amber-800",
  done: "bg-green-100 text-green-800",
  missed: "bg-orange-100 text-orange-800",
  cancelled: "bg-red-100 text-red-800",
};

const DEFAULT_FILTERS = {
  date_from: "",
  date_to: "",
  student_id: "all",
  appointment_type: "all",
  status_bucket: "all",
  group_by: "none",
};

function KpiCard({ icon: Icon, label, value, color }) {
  return (
    <div
      data-testid={`kpi-${label.toLowerCase().replace(/\s+/g, "-")}`}
      className={"card-surface p-4 sm:p-5 flex items-center gap-3 " + color}
    >
      <div className="h-10 w-10 rounded-xl bg-white border border-neutral-200 flex items-center justify-center shrink-0">
        <Icon className="h-5 w-5" />
      </div>
      <div className="min-w-0">
        <div className="text-[11px] uppercase tracking-wider font-semibold text-neutral-500">
          {label}
        </div>
        <div className="text-3xl font-bold leading-none mt-1 font-mono">{value}</div>
      </div>
    </div>
  );
}

function toCSV(rows) {
  if (!rows.length) return "";
  const headers = Object.keys(rows[0]);
  const escape = (v) => {
    const s = v == null ? "" : String(v);
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const lines = [headers.join(",")];
  for (const r of rows) lines.push(headers.map((h) => escape(r[h])).join(","));
  return lines.join("\n");
}

function downloadCSV(filename, csv) {
  const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

export default function ReportsTab() {
  const [filters, setFilters] = useState({
    ...DEFAULT_FILTERS,
    // sensible default: last 30 days → next 60 days
    date_from: todayISO(),
  });
  const [pending, setPending] = useState(filters);
  const [students, setStudents] = useState([]);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get("/students").then((r) => setStudents(r.data)).catch(() => {});
  }, []);

  const fetchReport = useCallback(async (f) => {
    setLoading(true);
    try {
      const params = {};
      if (f.date_from) params.date_from = f.date_from;
      if (f.date_to) params.date_to = f.date_to;
      if (f.student_id && f.student_id !== "all") params.student_id = f.student_id;
      if (f.appointment_type && f.appointment_type !== "all") params.appointment_type = f.appointment_type;
      if (f.status_bucket && f.status_bucket !== "all") params.status_bucket = f.status_bucket;
      if (f.group_by && f.group_by !== "none") params.group_by = f.group_by;
      const { data: res } = await api.get("/reports/appointments", { params });
      setData(res);
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  }, []);

  // Auto-load on mount with default filters
  useEffect(() => {
    fetchReport(filters);
  }, [fetchReport, filters]);

  const applyFilters = () => {
    setFilters(pending);
  };

  const resetFilters = () => {
    setPending(DEFAULT_FILTERS);
    setFilters(DEFAULT_FILTERS);
  };

  const exportCsv = () => {
    if (!data?.items?.length) return;
    const rows = data.items.map((i) => ({
      Alumno: i.student_name,
      Tipo: APPOINTMENT_TYPE_LABEL[i.appointment_type] || i.appointment_type,
      Fecha: i.date,
      Hora: i.time,
      "Hora fin": i.end_time,
      Inicio: i.pickup_location,
      Fin: i.pickup_location_end || i.pickup_location,
      Ruta: i.circuit,
      Estado: STATUS_LABEL[i.status_bucket] || i.status,
    }));
    const csv = toCSV(rows);
    downloadCSV(`reporte-citas-${todayISO()}.csv`, csv);
  };

  const printReport = () => window.print();

  const activeStudents = useMemo(() => students.filter((s) => s.active), [students]);

  return (
    <div className="space-y-6">
      {/* Filters */}
      <div className="card-surface p-4 sm:p-5 print:hidden">
        <div className="flex items-center gap-2 mb-4">
          <Filter className="h-4 w-4 text-neutral-500" />
          <h2 className="text-base font-bold">Filtros</h2>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
          <div>
            <label className="text-xs font-semibold text-neutral-600 mb-1 block">Desde</label>
            <input
              data-testid="report-date-from"
              type="date"
              value={pending.date_from}
              onChange={(e) => setPending({ ...pending, date_from: e.target.value })}
              className="w-full rounded-lg border border-neutral-300 bg-white px-2 h-10 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500"
            />
          </div>
          <div>
            <label className="text-xs font-semibold text-neutral-600 mb-1 block">Hasta</label>
            <input
              data-testid="report-date-to"
              type="date"
              value={pending.date_to}
              onChange={(e) => setPending({ ...pending, date_to: e.target.value })}
              className="w-full rounded-lg border border-neutral-300 bg-white px-2 h-10 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500"
            />
          </div>
          <div>
            <label className="text-xs font-semibold text-neutral-600 mb-1 block">Alumno</label>
            <StudentPicker
              value={pending.student_id}
              onChange={(v) => setPending({ ...pending, student_id: v })}
              students={activeStudents}
              testId="report-student-select"
              placeholder="Todos"
              allOption={{ value: "all", label: "Todos" }}
              triggerClassName="h-10 rounded-lg"
            />
          </div>
          <div>
            <label className="text-xs font-semibold text-neutral-600 mb-1 block">Tipo</label>
            <Select
              value={pending.appointment_type}
              onValueChange={(v) => setPending({ ...pending, appointment_type: v })}
            >
              <SelectTrigger data-testid="report-type-select" className="h-10 rounded-lg text-sm">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todas</SelectItem>
                {APPOINTMENT_TYPES.map((t) => (
                  <SelectItem key={t.value} value={t.value}>
                    {t.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-xs font-semibold text-neutral-600 mb-1 block">Estado</label>
            <Select
              value={pending.status_bucket}
              onValueChange={(v) => setPending({ ...pending, status_bucket: v })}
            >
              <SelectTrigger data-testid="report-status-select" className="h-10 rounded-lg text-sm">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {STATUS_BUCKETS.map((s) => (
                  <SelectItem key={s.value} value={s.value}>
                    {s.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-xs font-semibold text-neutral-600 mb-1 block">Agrupar</label>
            <Select
              value={pending.group_by}
              onValueChange={(v) => setPending({ ...pending, group_by: v })}
            >
              <SelectTrigger data-testid="report-group-select" className="h-10 rounded-lg text-sm">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {GROUP_OPTIONS.map((g) => (
                  <SelectItem key={g.value} value={g.value}>
                    {g.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
        <div className="flex flex-wrap gap-2 mt-4">
          <button
            data-testid="report-apply"
            onClick={applyFilters}
            disabled={loading}
            className="btn-primary"
          >
            <RefreshCw className={"h-4 w-4 " + (loading ? "animate-spin" : "")} />
            Aplicar filtros
          </button>
          <button
            data-testid="report-reset"
            onClick={resetFilters}
            className="btn-secondary"
          >
            Limpiar
          </button>
          <div className="ml-auto flex gap-2">
            <button
              data-testid="report-export-csv"
              onClick={exportCsv}
              disabled={!data?.items?.length}
              className="btn-secondary disabled:opacity-50"
            >
              <FileDown className="h-4 w-4" />
              Exportar CSV / Excel
            </button>
            <button
              data-testid="report-print"
              onClick={printReport}
              disabled={!data?.items?.length}
              className="btn-secondary disabled:opacity-50"
            >
              <Printer className="h-4 w-4" />
              Imprimir / PDF
            </button>
          </div>
        </div>
      </div>

      {/* KPI cards */}
      {data && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          <KpiCard
            icon={CalendarClock}
            label="Programadas"
            value={data.kpis.scheduled}
            color="text-amber-700"
          />
          <KpiCard
            icon={CheckCircle2}
            label="Realizadas"
            value={data.kpis.done}
            color="text-green-700"
          />
          <KpiCard
            icon={AlertCircle}
            label="No asistidas"
            value={data.kpis.missed}
            color="text-orange-700"
          />
          <KpiCard
            icon={XCircle}
            label="Canceladas"
            value={data.kpis.cancelled}
            color="text-red-700"
          />
        </div>
      )}

      {/* Groups */}
      {data?.groups && (
        <div className="card-surface p-4 sm:p-5">
          <div className="flex items-center gap-2 mb-3">
            <GraduationCap className="h-4 w-4 text-neutral-500" />
            <h3 className="font-bold text-sm">
              Agrupado {data.groups.by === "student" ? "por alumno" : "por tipo"}
            </h3>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm table-fixed">
              <colgroup>
                <col />
                <col style={{ width: "100px" }} />
                <col style={{ width: "130px" }} />
                <col style={{ width: "130px" }} />
                <col style={{ width: "130px" }} />
                <col style={{ width: "130px" }} />
              </colgroup>
              <thead className="bg-neutral-50 text-neutral-600 text-left">
                <tr>
                  <th className="px-4 py-3 font-semibold">
                    {data.groups.by === "student" ? "Alumno" : "Tipo"}
                  </th>
                  <th className="px-4 py-3 font-semibold text-right">Total</th>
                  <th className="px-4 py-3 font-semibold text-right">Programadas</th>
                  <th className="px-4 py-3 font-semibold text-right">Realizadas</th>
                  <th className="px-4 py-3 font-semibold text-right">No asistidas</th>
                  <th className="px-4 py-3 font-semibold text-right">Canceladas</th>
                </tr>
              </thead>
              <tbody>
                {data.groups.buckets.map((b) => (
                  <tr key={b.key} className="border-t border-neutral-100 h-[56px]">
                    <td className="px-4 py-3 font-semibold align-middle truncate" title={b.key}>
                      {data.groups.by === "type"
                        ? APPOINTMENT_TYPE_LABEL[b.key] || b.key
                        : b.key}
                    </td>
                    <td className="px-4 py-3 text-right font-mono font-bold align-middle">{b.count}</td>
                    <td className="px-4 py-3 text-right font-mono text-amber-700 align-middle">{b.scheduled}</td>
                    <td className="px-4 py-3 text-right font-mono text-green-700 align-middle">{b.done}</td>
                    <td className="px-4 py-3 text-right font-mono text-orange-700 align-middle">{b.missed}</td>
                    <td className="px-4 py-3 text-right font-mono text-red-700 align-middle">{b.cancelled}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Detail table */}
      {data && (
        <div className="card-surface overflow-hidden">
          <div className="px-4 sm:px-5 py-3 border-b border-neutral-100 flex items-center justify-between">
            <h3 className="font-bold text-sm">Detalle ({data.items.length} citas)</h3>
          </div>
          {data.items.length === 0 ? (
            <div className="p-10 text-center text-sm text-neutral-500">
              No hay citas con los filtros seleccionados.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-neutral-50 text-neutral-600 text-left">
                  <tr>
                    <th className="px-3 py-2 font-semibold">Alumno</th>
                    <th className="px-3 py-2 font-semibold">Tipo</th>
                    <th className="px-3 py-2 font-semibold">Fecha</th>
                    <th className="px-3 py-2 font-semibold">Hora</th>
                    <th className="px-3 py-2 font-semibold">Inicio</th>
                    <th className="px-3 py-2 font-semibold">Fin</th>
                    <th className="px-3 py-2 font-semibold">Ruta</th>
                    <th className="px-3 py-2 font-semibold">Estado</th>
                  </tr>
                </thead>
                <tbody>
                  {data.items.map((it) => (
                    <tr
                      key={it.id}
                      data-testid={`report-row-${it.id}`}
                      className="border-t border-neutral-100 hover:bg-neutral-50/60"
                    >
                      <td className="px-3 py-2 font-semibold">{it.student_name}</td>
                      <td className="px-3 py-2 text-neutral-600">
                        {APPOINTMENT_TYPE_LABEL[it.appointment_type] || it.appointment_type}
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap">{formatDateES(it.date)}</td>
                      <td className="px-3 py-2 font-mono">
                        {it.time}
                        {it.end_time && <span className="text-neutral-400"> – {it.end_time}</span>}
                      </td>
                      <td className="px-3 py-2 text-neutral-600">{it.pickup_location}</td>
                      <td className="px-3 py-2 text-neutral-600" data-testid={`report-end-${it.id}`}>
                        {it.pickup_location_end || it.pickup_location}
                      </td>
                      <td className="px-3 py-2 text-neutral-600">{it.circuit}</td>
                      <td className="px-3 py-2">
                        <span
                          className={
                            "inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold " +
                            (STATUS_PILL[it.status_bucket] || "bg-neutral-100 text-neutral-700")
                          }
                        >
                          {STATUS_LABEL[it.status_bucket] || it.status}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
